import { useCallback, useEffect, useRef, useState } from 'react';
import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';
import { normalizePages } from '../utils/actionBarLayout';

const fromStored = (stored) => ({ pages: normalizePages(stored?.pages), revision: Number.isInteger(stored?.revision) ? stored.revision : 0 });

/**
 * The character's saved action bar layout. Saves run one at a time against the latest revision; a conflict
 * (another open sheet rearranged the bar) adopts the server's layout instead of overwriting it.
 */
export default function useActionBarLayout(characterId, storedLayout) {
  const [layout, setLayout] = useState(() => fromStored(storedLayout));
  const revision = useRef(layout.revision);
  const queue = useRef(Promise.resolve());
  const live = useRef(true);
  useEffect(() => () => { live.current = false; }, []);

  // A newer layout from a character reload wins over what this sheet last saw.
  useEffect(() => {
    const next = fromStored(storedLayout);
    if (next.revision > revision.current) { revision.current = next.revision; setLayout(next); }
  }, [storedLayout]);

  const save = useCallback((pages) => {
    const wanted = normalizePages(pages);
    setLayout((previous) => ({ ...previous, pages: wanted }));
    if (!characterId) return Promise.resolve();
    queue.current = queue.current.then(async () => {
      try {
        const response = await apiFetch(`/characters/${encodeURIComponent(characterId)}/action-bar-layout`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pages: wanted, expectedRevision: revision.current }),
        });
        const result = await response.json().catch(() => ({}));
        if (response.status === 409) {
          const latest = fromStored(result.actionBarLayout);
          revision.current = latest.revision;
          if (live.current) setLayout(latest);
          notify('Your action bar was rearranged on another screen; showing that layout.', 'warning');
          return;
        }
        if (!response.ok) throw new Error(result.message || 'The action bar layout could not be saved.');
        const saved = fromStored(result.actionBarLayout);
        revision.current = saved.revision;
        if (live.current) setLayout((previous) => ({ ...previous, revision: saved.revision }));
      } catch (error) {
        notify(error.message || 'The action bar layout could not be saved.', 'danger');
      }
    });
    return queue.current;
  }, [characterId]);

  return { pages: layout.pages, save };
}
